import { ThemeProvider } from '@emotion/react'
import { Box, Button, Chip, CircularProgress, Container, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material'
import MUIDataTable from 'mui-datatables'
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import api from '../../services/api'
import theme from '../../themeDash'
import EmpresaSelect from './EmpresaSelect'
import SourceSelect from '../SourceSelect'
import KPICard from '../KPICard'
import DateTimeToolbar from '../DateTimeToolbar'
import moment from 'moment'
import { textLabels } from '../textLabelsPT'
import { blue, blueGrey, green, grey, lightBlue, lime, orange, red } from '@mui/material/colors'
import { ContactPhone, Visibility } from '@mui/icons-material'
import FormDialog from './FormDialog'

const PERIOD_MAP = {
  '24h': { value: 1, unit: 'days' },
  '7d': { value: 7, unit: 'days' },
  '30d': { value: 30, unit: 'days' },
}

const SEV_COLORS = {
  1: blueGrey[400],
  2: lightBlue[400],
  3: lime[600],
  4: orange[500],
  5: red[500],
}

function formatHHmm(minutes) {
  if (!minutes && minutes !== 0) return '-'
  const h = Math.floor(Math.abs(minutes) / 60)
  const m = Math.round(Math.abs(minutes) % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export default function () {
  const [empresas, setEmpresas] = useState([])
  const [allEmpresas, setAllEmpresas] = useState([])
  const [source, setSource] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({ results: [], total: 0, limit: 30 })
  const [comparison, setComparison] = useState(null)
  const [period, setPeriod] = useState('24h')
  const didAutoLoad = useRef(false)

  // New filter states
  const [acked, setAcked] = useState(null)       // null=todos, true=vistados, false=nao vistados
  const [severities, setSeverities] = useState([]) // empty = sem filtro

  // FormDialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState('acks')
  const [dialogData, setDialogData] = useState([])

  useLayoutEffect(() => {
    document.title = "Horus - Relatórios de Alarmes"
  })

  // Auto-load: fetch all empresas on mount then trigger first fetch
  useEffect(() => {
    Promise.all([
      api.get('/empresa-athena/list').catch(() => ({ data: [] })),
      api.get('/empresa/all').catch(() => ({ data: [] })),
    ]).then(([athenaRes, horusRes]) => {
      const seen = new Set()
      const codes = []
      athenaRes.data.forEach(e => {
        if (!seen.has(e.cod_empresa)) { codes.push(e.cod_empresa); seen.add(e.cod_empresa) }
      })
      horusRes.data
        .filter(e => e.status === 1)
        .forEach(e => {
          if (!seen.has(e.cod_empresa)) { codes.push(e.cod_empresa); seen.add(e.cod_empresa) }
        })
      setAllEmpresas(codes)
      setEmpresas(codes)
    }).catch(err => console.error('Error fetching empresas:', err))
  }, [])

  // When allEmpresas are loaded, auto-fetch
  useEffect(() => {
    if (allEmpresas.length > 0 && period && !didAutoLoad.current) {
      didAutoLoad.current = true
      fetchWithPeriod(period, allEmpresas)
    }
  }, [allEmpresas])

  const handlePeriod = (_, val) => {
    if (!val) return
    setPeriod(val)
    const emps = empresas.length > 0 ? empresas : allEmpresas
    fetchWithPeriod(val, emps)
  }

  const fetchWithPeriod = (p, emps, page = 0, limit = 30) => {
    const { value, unit } = PERIOD_MAP[p]
    const end = moment().toISOString()
    const start = moment().subtract(value, unit).toISOString()
    doFetch(emps, start, end, page, limit)
  }

  const handleManualSearch = () => {
    setPeriod(null)
    doFetch(empresas, startDate, endDate, 0, data.limit)
  }

  const openDialog = (type, data) => {
    setDialogType(type)
    setDialogData(data)
    setDialogOpen(true)
  }

  const doFetch = (emps, start, end, page = 0, limit = 30) => {
    if (!emps.length || !start || !end) return
    setData({ results: [], total: 0, limit })
    const skip = page * limit
    setLoading(true)

    const payload = {
      empresas: emps, start, end, source, skip, limit,
      acked: acked,
      severities: severities.length > 0 ? severities : null,
    }

    Promise.all([
      api.post('report/alarmes', payload),
      api.post('report/alarmes/comparison', { empresas: emps, start, end, source }),
    ])
      .then(([alarmsRes, compRes]) => {
        setData(d => ({ ...d, results: alarmsRes.data.results, total: alarmsRes.data.total, limit }))
        setComparison(compRes.data)
      })
      .catch((err) => {
        console.log(err)
        setData({ results: [], total: 0, limit: 30 })
      })
      .finally(() => setLoading(false))
  }

  // Re-fetch when acked or severities change
  const handleAckedChange = (_, val) => {
    const newVal = val // null, 'true', 'false'
    const parsed = newVal === 'true' ? true : newVal === 'false' ? false : null
    setAcked(parsed)
    const emps = empresas.length > 0 ? empresas : allEmpresas
    // Defer fetch to next tick so state updates
    setTimeout(() => {
      const p = { empresas: emps, start: startDate, end: endDate, source, skip: 0, limit: data.limit, acked: parsed, severities: severities.length > 0 ? severities : null }
      if (period) {
        const { value, unit } = PERIOD_MAP[period]
        p.end = moment().toISOString()
        p.start = moment().subtract(value, unit).toISOString()
      }
      if (!emps.length || !p.start || !p.end) return
      setLoading(true)
      setData(d => ({ ...d, results: [], total: 0 }))
      Promise.all([
        api.post('report/alarmes', p),
        api.post('report/alarmes/comparison', { empresas: emps, start: p.start, end: p.end, source }),
      ])
        .then(([alarmsRes, compRes]) => {
          setData(d => ({ ...d, results: alarmsRes.data.results, total: alarmsRes.data.total }))
          setComparison(compRes.data)
        })
        .catch(() => setData({ results: [], total: 0, limit: 30 }))
        .finally(() => setLoading(false))
    }, 0)
  }

  const toggleSeverity = (sev) => {
    const next = severities.includes(sev)
      ? severities.filter(s => s !== sev)
      : [...severities, sev]
    setSeverities(next)
    // Re-fetch
    const emps = empresas.length > 0 ? empresas : allEmpresas
    setTimeout(() => {
      const p = { empresas: emps, start: startDate, end: endDate, source, skip: 0, limit: data.limit, acked, severities: next.length > 0 ? next : null }
      if (period) {
        const { value, unit } = PERIOD_MAP[period]
        p.end = moment().toISOString()
        p.start = moment().subtract(value, unit).toISOString()
      }
      if (!emps.length || !p.start || !p.end) return
      setLoading(true)
      setData(d => ({ ...d, results: [], total: 0 }))
      api.post('report/alarmes', p)
        .then(res => setData(d => ({ ...d, results: res.data.results, total: res.data.total })))
        .catch(() => setData({ results: [], total: 0, limit: 30 }))
        .finally(() => setLoading(false))
    }, 0)
  }

  const columns = [
    {
      name: "source",
      label: "Fonte",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <Chip
            label={value === 'wazuh' ? 'Wazuh' : 'Zabbix'}
            size="small"
            sx={{
              bgcolor: value === 'wazuh' ? orange[700] : blue[600],
              color: '#fff',
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        ),
      }
    },
    {
      name: "cod_empresa",
      label: "Empresa",
      options: {
        filter: true,
        sort: true,
        customFilterListOptions: { render: v => `Empresa: ${v}` },
      }
    },
    {
      name: "hostname",
      label: "Host",
      options: { filter: false, sort: true }
    },
    {
      name: "name",
      label: "Descrição",
      options: { filter: false, sort: false }
    },
    {
      name: "severity",
      label: "Severidade",
      options: { filter: true, sort: true }
    },
    {
      name: "clock",
      label: "Quando",
      options: {
        filter: false,
        sort: true,
        customBodyRender: value => <span>{value ? moment(value).locale('pt-br').format('DD-MM-YYYY HH:mm:ss') : '-'}</span>,
      }
    },
    {
      name: "resolved_at",
      label: "Finalizado",
      options: {
        filter: false,
        sort: true,
        customBodyRender: value => <span>{value ? moment(value).locale('pt-br').format('DD-MM-YYYY HH:mm:ss') : '-'}</span>,
      }
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const map = {
            active: { label: 'Ativo', color: green[600] },
            resolved: { label: 'Resolvido', color: blue[600] },
            dismissed: { label: 'Dispensado', color: orange[700] },
          }
          const cfg = map[value] || { label: value, color: grey[500] }
          return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.color, color: '#fff', fontWeight: 600, fontSize: 11 }} />
        },
      }
    },
    {
      name: "resolved_by_name",
      label: "Dispensado por",
      options: { filter: false, sort: true }
    },
    {
      name: "time_total",
      label: "Duração",
      options: {
        filter: false,
        sort: true,
        customBodyRender: value => <span>{formatHHmm(value)}</span>,
      }
    },
    {
      name: "online_users",
      label: "Usuários Online",
      options: {
        filter: false,
        sort: false,
        customBodyRender: users => users?.map(u => u?.name).join(', ') || ''
      }
    },
    {
      name: "acks",
      label: "Ack",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (value?.length ? <IconButton sx={{ color: grey[900] }} onClick={() => openDialog('acks', value)}>
            <Visibility />
          </IconButton> : "")
        }
      }
    },
    {
      name: "ppi_executions",
      label: "PPI",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value || !value.length) return ''
          return (
            <Chip
              label={`${value.length} exec`}
              size="small"
              sx={{ fontWeight: 600, fontSize: 11, bgcolor: green[600], color: '#fff', cursor: 'pointer' }}
              onClick={() => openDialog('executions', value)}
            />
          )
        }
      }
    },
    {
      name: "escalations",
      label: "Contatos",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value || !value.length) return ''
          return (
            <Chip
              icon={<ContactPhone sx={{ fontSize: 14, color: '#fff !important' }} />}
              label={`${value.length}`}
              size="small"
              sx={{ fontWeight: 600, fontSize: 11, bgcolor: blue[600], color: '#fff', cursor: 'pointer' }}
              onClick={() => openDialog('escalations', value)}
            />
          )
        }
      }
    },
    {
      name: "reaction_time",
      label: "Tempo de Reação",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => {
          if (value === null || value === undefined) return ''
          return <span style={{ color: value > 45 ? red[800] : 'inherit' }}>{formatHHmm(value)}</span>
        },
      }
    },
  ]

  const handleTableChange = (action, tableState) => {
    const { page, rowsPerPage } = tableState
    const emps = empresas.length > 0 ? empresas : allEmpresas
    switch (action) {
      case 'changePage':
        if (period) {
          fetchWithPeriod(period, emps, page, rowsPerPage)
        } else {
          doFetch(emps, startDate, endDate, page, rowsPerPage)
        }
        break
      case 'changeRowsPerPage':
        if (period) {
          fetchWithPeriod(period, emps, 0, rowsPerPage)
        } else {
          doFetch(emps, startDate, endDate, 0, rowsPerPage)
        }
        break
      default:
        break
    }
  }

  const options = {
    search: false,
    textLabels: textLabels,
    filterType: 'checkbox',
    rowsPerPageOptions: [10, 30, 50, 100, 500, 1000, 5000],
    rowsPerPage: data.limit,
    count: data.total,
    serverSide: true,
    rowHover: false,
    print: true,
    selectableRows: 'none',
    download: true,
    customToolbarSelect: () => { },
    onTableChange: handleTableChange,
    setRowProps: (row) => {
      const sev = Number(row[4])
      const colorMap = {
        5: { backgroundColor: theme.palette.mode === 'dark' ? red[400] : red[300] },
        4: { backgroundColor: theme.palette.mode === 'dark' ? orange[300] : orange[200] },
        3: { backgroundColor: theme.palette.mode === 'dark' ? lime[300] : lime[200] },
        2: { backgroundColor: theme.palette.mode === 'dark' ? lightBlue[300] : lightBlue[200] },
        1: { backgroundColor: theme.palette.mode === 'dark' ? blueGrey[300] : blueGrey[200] },
      }
      return colorMap[sev] ? { style: colorMap[sev] } : {}
    },
    onDownload: (buildHead, buildBody, columns) => {
      const customData = data?.results.map((row, i) => ({
        index: i,
        data: [
          row.source || '',
          row.cod_empresa || '',
          row.hostname || '',
          row.name || '',
          row.severity || '',
          row.clock ? moment(row.clock).format('DD-MM-YYYY HH:mm:ss') : '',
          row.resolved_at ? moment(row.resolved_at).format('DD-MM-YYYY HH:mm:ss') : '',
          row.status || '',
          row.resolved_by_name || '',
          formatHHmm(row.time_total),
          row.online_users?.map(u => u.name).join(', ') || '',
          row.acks?.map(a => a.description).join(', ') || '',
          row.ppi_executions?.length ? `${row.ppi_executions.length} exec` : '',
          row.escalations?.length ? `${row.escalations.length} contatos` : '',
          formatHHmm(row.reaction_time),
        ]
      }))
      return "\uFEFF" + buildHead(columns) + buildBody(customData)
    }
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }} maxWidth={false}>
      <ThemeProvider theme={theme}>
        <FormDialog
          open={dialogOpen}
          handlerClose={() => { setDialogOpen(false); setDialogData([]) }}
          type={dialogType}
          data={dialogData}
        />

        {/* Toolbar - Line 1: Period, Source, Acked, Severity, Dates, Buscar */}
        <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ bgcolor: 'background.paper', p: 1, boxShadow: 1, mb: 1 }}>
          <ToggleButtonGroup value={period} exclusive onChange={handlePeriod} size="small" sx={{ m: 0.5 }}>
            <ToggleButton value="24h" sx={{ color: grey[300] }}>24h</ToggleButton>
            <ToggleButton value="7d" sx={{ color: grey[300] }}>7d</ToggleButton>
            <ToggleButton value="30d" sx={{ color: grey[300] }}>30d</ToggleButton>
          </ToggleButtonGroup>

          <SourceSelect value={source} handler={setSource} />

          <ToggleButtonGroup
            value={acked === null ? 'todos' : acked ? 'true' : 'false'}
            exclusive
            onChange={handleAckedChange}
            size="small"
            sx={{ m: 0.5 }}
          >
            <ToggleButton value="todos" sx={{ color: grey[300] }}>Todos</ToggleButton>
            <ToggleButton value="true" sx={{ color: grey[300] }}>Vistados</ToggleButton>
            <ToggleButton value="false" sx={{ color: grey[300] }}>Não Vistados</ToggleButton>
          </ToggleButtonGroup>

          <Box display="flex" alignItems="center" gap={0.5} sx={{ m: 0.5 }}>
            {[1, 2, 3, 4, 5].map(sev => (
              <Chip
                key={sev}
                label={sev}
                size="small"
                variant={severities.includes(sev) ? 'filled' : 'outlined'}
                onClick={() => toggleSeverity(sev)}
                sx={{
                  fontWeight: 700,
                  bgcolor: severities.includes(sev) ? SEV_COLORS[sev] : 'transparent',
                  color: severities.includes(sev) ? '#fff' : SEV_COLORS[sev],
                  borderColor: SEV_COLORS[sev],
                  cursor: 'pointer',
                  minWidth: 32,
                }}
              />
            ))}
          </Box>

          <Box sx={{ mx: 0.5, borderLeft: '1px solid', borderColor: 'divider', pl: 1, display: 'flex', alignItems: 'center' }}>
            <DateTimeToolbar label={'Data Inicial'} handler={(v) => { setStartDate(v); setPeriod(null) }} initValue={moment().subtract(1, 'days')} />
            <DateTimeToolbar label={'Data Final'} handler={(v) => { setEndDate(v); setPeriod(null) }} />
            <Button onClick={handleManualSearch} disabled={
              !(startDate) || !(endDate) || !empresas.length || loading
            }
              variant="contained" color="primary" size="small" sx={{ ml: 0.5 }}>Buscar</Button>
          </Box>
        </Box>

        {/* Toolbar - Line 2: Empresa chips */}
        <Box sx={{ bgcolor: 'background.paper', p: 1, boxShadow: 1, mb: 2 }}>
          <EmpresaSelect handler={setEmpresas} selected={empresas} />
        </Box>

        {/* KPI Cards */}
        {comparison && (
          <Box display="flex" flexWrap="wrap" justifyContent="center" mb={2}>
            <KPICard title="Total Alarmes" value={comparison.current?.total}
              change={comparison.variation?.total_pct} />
            <KPICard title="MTTA" value={comparison.current?.mtta ? formatHHmm(comparison.current.mtta) : '--'}
              change={comparison.variation?.mtta_pct} />
            <KPICard title="MTTR" value={comparison.current?.mttr ? formatHHmm(comparison.current.mttr) : '--'}
              change={comparison.variation?.mttr_pct} />
            <KPICard title="Resolvidos" value={comparison.current?.resolved_pct} unit="%"
              change={comparison.variation?.resolved_pct} invertColors />
          </Box>
        )}

        {/* Table */}
        <Box>
          <MUIDataTable
            title="Histórico de Alarmes"
            data={data?.results}
            columns={columns}
            options={{
              ...options,
              textLabels: {
                ...textLabels,
                body: {
                  noMatch: loading
                    ? <CircularProgress color="info" />
                    : "Nenhum registro encontrado"
                }
              }
            }}
          />
        </Box>
      </ThemeProvider>
    </Container>
  )
}
